import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { languageTransfer } from "../../utils/languageTransfer";
import GAMEITEM from "../../components/GAMEITEM/GAMEITEM";
import i18next from "i18next";
import TopTab from "../Home/components/topTab";
import MobileSearch from "../Search/mobile-search";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroller";
import useWindowWidth from "../../hook/useWindowWidth";
import LoadingBlock from "../../components/Loading/LoadingBlock";
import useDeviceType from "../../hook/useDeviceType";
import useCasinoType from "../../hook/useCasinoType";
import { filterType } from "../../hook/useGameListFilter";
import NotFound from "../../components/ProgressBlock/NotFound";
import LoaderGameList from "../../components/Loading/LoadingGameList";
import { isLoggedIn } from "../../utils/isLoggedIn";
import { getUserGameClickList } from "../../api/getApi";

const GameSubTypeList = () => {
  const { t } = useTranslation();
  const i18n_common = (key) => t(`common.${key}`);

  const { subType, gameType, gamePlatform } = useParams();
  const windowWidth = useWindowWidth();
  const casinoType = useCasinoType();

  const isMobile = useDeviceType() === "Mobile";

  const handleAllGame = useSelector((state) => state.isHandleAllGame);
  const {
    topGameList = [],
    newGameList = [],
    allGames = [],
    categorizedGames,
    sortedByPlatformAndCategoryGames,
    favoriteGameList = [],
    exclusiveBrandGames = [],
    sortedByPlatformGames = [],
  } = handleAllGame;

  const [userClickGames, setUserClickGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isLoggedIn()) {
      getUserGameClickList() //取得點擊過的遊戲 來自這個api
        .then((data) => {
          setUserClickGames(data.userUserGameClick || []);
        })
        .catch(() => {})
        .finally(() => {});
    }
  }, []);

  const gameListFilter = useMemo(() => {
    switch (subType) {
      case "News":
        return [...newGameList, ...allGames];
      case "Top":
        return [...topGameList, ...allGames];
      case "Home":
        return allGames;
      case "AllGames":
        if (gamePlatform !== "all" && gamePlatform) {
          return sortedByPlatformGames?.[gamePlatform];
        } else {
          return allGames;
        }
      case "ExclusiveGame":
        return exclusiveBrandGames;
      case "ClickedGames": // 增加 點擊過遊戲的subType
        return userClickGames; // 過濾出 userClickGames 中的內容
      default:
        if (
          gamePlatform &&
          gamePlatform !== "all" &&
          subType !== "MyFavorite"
        ) {
          return sortedByPlatformAndCategoryGames?.[gamePlatform]?.[subType];
        } else {
          if (subType === "MyFavorite" && isLoggedIn()) {
            return favoriteGameList;
          }
          return categorizedGames?.[subType];
        }
    }
  }, [subType, categorizedGames, userClickGames, gamePlatform]); //加上gamePlatform讓他可以根據platform重新render

  const resultGameList = useMemo(() => {
    return filterType({
      gameList: gameListFilter,
      casinoType: casinoType,
    });
  }, [gameListFilter, gamePlatform]); //加上gamePlatform讓他可以根據platform重新render

  const [displayedItems, setDisplayedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
    setIsLoading(true);
  }, [subType, gamePlatform]);

  const ITEMS_PER_PAGE = useMemo(() => {
    if (windowWidth >= 1280) {
      return 32;
    } else if (windowWidth >= 1024) {
      return 35;
    } else if (windowWidth >= 768) {
      return 25;
    } else {
      return 12;
    }
  }, [windowWidth]);

  useEffect(() => {
    setDisplayedItems(resultGameList?.slice(0, ITEMS_PER_PAGE));
  }, [resultGameList, ITEMS_PER_PAGE]);

  const loadMore = useCallback(() => {
    setTimeout(() => {
      const moreItems = resultGameList?.slice(
        0,
        (currentPage + 1) * ITEMS_PER_PAGE
      );
      if (moreItems?.length >= displayedItems?.length) {
        setDisplayedItems(moreItems);
        setIsLoading(false);
        setCurrentPage((prevPage) => prevPage + 1);
      }
    }, 300);
  }, [currentPage, displayedItems?.length]);

  const hasMoreItems = useMemo(() => {
    return displayedItems?.length < resultGameList?.length;
  }, [displayedItems?.length]);

  useEffect(() => {
    if (isLoading) {
      setTimeout(() => {
        setIsLoading(false);
      }, 1500);
    }
  }, [isLoading]);
  return (
    <>
      <section className="md:hidden flex flex-col gap-[10px]">
        <TopTab />
        <MobileSearch />
      </section>

      <div className="mb-[8px] h-[30px] md:h-[56px] flex items-center">
        <div className="mx-[16px] text-base-light-dark dark:text-base-light-white font-semibold text-lg">
          {gamePlatform && subType === "AllGames" && gamePlatform !== "all"
            ? gamePlatform
            : ""}{" "}
          {i18n_common(subType)}
        </div>
      </div>
      <InfiniteScroll
        pageStart={0}
        loadMore={loadMore}
        hasMore={hasMoreItems}
        useWindow={isMobile}
        loader={<LoadingBlock commonLoading={true} />}
      >
        <LoadingBlock commonLoading={isLoading}>
          {displayedItems?.length === 0 ? (
            <NotFound />
          ) : (
            <section className="grid lg:grid-cols-7 md:grid-cols-5 grid-cols-3 xl:grid-cols-8 md:gap-x-[16px] gap-[10px] gap-y-[24px] px-common-padding-x">
              {displayedItems?.map((item, index) => {
                const gameName =
                  item?.[
                    `gameName${languageTransfer({
                      lang: i18next.language,
                    })}`
                  ];
                const gameNameEn = item?.gameNameEn;
                const platform = item?.game_platform;
                const gameSubType = item?.game_subtype;

                const gameCode = item?.gameCode;
                const gameType = item?.gameType;
                return (
                  <GAMEITEM
                    forceShow={subType === "MyFavorite"}
                    gameProps={{
                      gameName,
                      platform,
                      gameCode,
                      gameType,
                      gameNameEn,
                      gameSubType,
                    }}
                    originProps={item}
                    itemClass={`!h-[155px]`}
                    index={index}
                  />
                );
              })}
            </section>
          )}
        </LoadingBlock>
      </InfiniteScroll>
    </>
  );
};

export default GameSubTypeList;
